/**
 * useTransactions Hook
 * 
 * React Query hook for managing cash flow entries (transactions).
 * Handles CRUD operations with optimistic updates and intelligent caching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { queryKeys } from '../../../lib/react-query';
import type { CashFlowEntry } from '../../../types/database';
import { notify } from '../../../lib/notifications';
import { calculateRecurrenceDates } from '../../../lib/utils/recurring';

interface UseTransactionsOptions {
  companyIds: string | string[];
  userId: string;
}

interface TransactionFilters {
  search?: string;
  entryType?: 'income' | 'expense' | 'transfer';
  paymentStatus?: 'confirmado' | 'previsão';
  startDate?: string;
  endDate?: string;
  category?: string;
}

/**
 * Fetch transactions with filters
 * Supports multi-company filtering
 */
async function fetchTransactions(
  companyIds: string | string[],
  filters?: TransactionFilters
): Promise<CashFlowEntry[]> {
  let query = supabase
    .from('cash_flow_entries')
    .select('*')
    .eq('is_simulation', false)
    .order('entry_date', { ascending: false });

  // Handle multi-company filtering
  if (typeof companyIds === 'string') {
    query = query.eq('company_id', companyIds);
  } else if (Array.isArray(companyIds)) {
    if (companyIds.length === 0) {
      return [];
    } else if (companyIds.length === 1) {
      query = query.eq('company_id', companyIds[0]);
    } else {
      query = query.in('company_id', companyIds);
    }
  }

  // Apply filters
  if (filters?.search) {
    query = query.or(`description.ilike.%${filters.search}%,notes.ilike.%${filters.search}%`);
  }

  if (filters?.entryType) {
    query = query.eq('entry_type', filters.entryType);
  }

  if (filters?.paymentStatus) {
    query = query.eq('payment_status', filters.paymentStatus);
  }

  if (filters?.startDate) {
    query = query.gte('entry_date', filters.startDate);
  }

  if (filters?.endDate) {
    query = query.lte('entry_date', filters.endDate);
  }

  if (filters?.category) {
    query = query.eq('main_category', filters.category);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[useTransactions] Error fetching transactions:', error);
    throw new Error(`Erro ao buscar lançamentos: ${error.message}`);
  }

  return data || [];
}

/**
 * Create new transaction (with recurring logic)
 */
async function createTransaction(
  entry: Omit<CashFlowEntry, 'id' | 'created_at' | 'updated_at'>
): Promise<CashFlowEntry> {
  // ✅ COMPATIBILIDADE: Preencher campo 'category' com 'main_category'
  // até que a migration seja executada para tornar 'category' nullable
  const entryWithCategory = {
    ...entry,
    category: entry.main_category, // Compatibilidade com banco
  } as any;

  // Debug: Log para verificar dados de recorrência
  console.log('[createTransaction] Entry data:', {
    is_recurring: entryWithCategory.is_recurring,
    frequency: entryWithCategory.frequency,
    recurrence_end_date: entryWithCategory.recurrence_end_date,
    category: entryWithCategory.category,
    main_category: entryWithCategory.main_category,
    full_entry: entryWithCategory
  });

  // If recurring, generate multiple entries
  if (entryWithCategory.is_recurring && entryWithCategory.frequency && entryWithCategory.entry_date) {
    console.log('[createTransaction] Generating recurring entries...');
    console.log('[createTransaction] Original payment_status:', entryWithCategory.payment_status);
    console.log('[createTransaction] Original entry_date:', entryWithCategory.entry_date);
    
    // Generate unique group ID for this recurrence series
    const recurrenceGroupId = crypto.randomUUID();
    console.log('[createTransaction] Generated recurrence_group_id:', recurrenceGroupId);
    
    // Calculate all recurrence dates
    console.log('[createTransaction] Starting from entry_date:', entryWithCategory.entry_date);
    console.log('[createTransaction] Ending at recurrence_end_date:', entryWithCategory.recurrence_end_date);
    console.log('[createTransaction] Frequency:', entryWithCategory.frequency);
    
    const dates = calculateRecurrenceDates(
      entryWithCategory.entry_date,
      entryWithCategory.recurrence_end_date,
      entryWithCategory.frequency
    );
    
    console.log(`[createTransaction] Generated ${dates.length} dates:`, dates.slice(0, 5), dates.length > 5 ? '...' : '');
    
    // Create all entries at once with the same group ID
    const today = new Date().toISOString().split('T')[0];
    
    const entries = dates.map((date, index) => {
      const entry = {
        ...entryWithCategory,
        entry_date: date,
        recurrence_group_id: recurrenceGroupId, // ✅ Same ID for all entries in series
        // Keep due_date and payment_date relative if they exist
        due_date: entryWithCategory.due_date ? addDays(date, daysBetween(entryWithCategory.entry_date!, entryWithCategory.due_date)) : null,
        payment_date: entryWithCategory.payment_date ? addDays(date, daysBetween(entryWithCategory.entry_date!, entryWithCategory.payment_date)) : null,
        // ✅ Apenas a primeira parcela mantém o status original, as futuras são sempre previsão
        payment_status: index === 0 ? entryWithCategory.payment_status : ('previsão' as const),
        // ✅ IMPORTANTE: Garantir que user_id e company_id estão presentes (RLS requirement)
        user_id: entryWithCategory.user_id,
        company_id: entryWithCategory.company_id,
      };
      
      if (index < 3) {
        console.log(`[createTransaction] Entry ${index + 1}/${dates.length}:`, {
          entry_date: entry.entry_date,
          payment_status: entry.payment_status,
          original_status: entryWithCategory.payment_status,
          is_future: date > today,
          user_id: entry.user_id,
          company_id: entry.company_id,
        });
      }
      
      return entry;
    });
    
    // Debug: Log first entry to verify all required fields
    console.log('[createTransaction] First entry being inserted:', {
      has_user_id: !!entries[0].user_id,
      has_company_id: !!entries[0].company_id,
      user_id: entries[0].user_id,
      company_id: entries[0].company_id,
      entry_date: entries[0].entry_date,
      payment_status: entries[0].payment_status,
    });
    
    const { data, error } = await supabase
      .from('cash_flow_entries')
      .insert(entries)
      .select();

    if (error) {
      console.error('[useTransactions] Error creating recurring transactions:', error);
      throw new Error(`Erro ao criar lançamentos recorrentes: ${error.message}`);
    }

    console.log(`[createTransaction] Created ${data.length} recurring entries successfully`);
    // Return first entry as the "main" one
    return data[0];
  }

  // Single entry (non-recurring)
  const { data, error } = await supabase
    .from('cash_flow_entries')
    .insert(entryWithCategory)
    .select()
    .single();

  if (error) {
    console.error('[useTransactions] Error creating transaction:', error);
    throw new Error(`Erro ao criar lançamento: ${error.message}`);
  }

  console.log('[createTransaction] Created successfully:', data);
  return data;
}

/**
 * Helper: Calculate days between two dates
 */
function daysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = d2.getTime() - d1.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Helper: Add days to a date
 */
function addDays(dateStr: string, days: number): string {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Update existing transaction
 */
async function updateTransaction(
  id: string,
  updates: Partial<Omit<CashFlowEntry, 'id' | 'created_at' | 'updated_at'>>
): Promise<CashFlowEntry> {
  // ✅ COMPATIBILIDADE: Se main_category foi atualizado, atualizar category também
  const updatesWithCategory = {
    ...updates,
    ...(updates.main_category ? { category: updates.main_category } : {}),
  } as any;

  // Buscar o lançamento original para verificar se está sendo convertido para recorrente
  const { data: original, error: fetchError } = await supabase
    .from('cash_flow_entries')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) {
    console.error('[useTransactions] Error fetching original transaction:', fetchError);
    throw new Error(`Erro ao buscar lançamento: ${fetchError.message}`);
  }

  // Se está sendo convertido de não-recorrente para recorrente, criar as recorrências
  if (!original.is_recurring && updatesWithCategory.is_recurring && updatesWithCategory.frequency) {
    console.log('[updateTransaction] Converting to recurring - creating recurrence series');
    
    // Gerar ID único para o grupo de recorrências
    const recurrenceGroupId = crypto.randomUUID();
    
    // Calcular datas de recorrência
    const dates = calculateRecurrenceDates(
      original.entry_date || new Date().toISOString().split('T')[0],
      updatesWithCategory.recurrence_end_date,
      updatesWithCategory.frequency
    );
    
    console.log(`[updateTransaction] Creating ${dates.length} recurring entries`);
    
    // Atualizar o lançamento original com o grupo de recorrência
    const updatedOriginal = {
      ...updatesWithCategory,
      recurrence_group_id: recurrenceGroupId,
    };
    
    const { data, error } = await supabase
      .from('cash_flow_entries')
      .update(updatedOriginal)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[useTransactions] Error updating to recurring:', error);
      throw new Error(`Erro ao atualizar lançamento: ${error.message}`);
    }

    // Criar os lançamentos futuros (pular a primeira data que é o lançamento original)
    if (dates.length > 1) {
      // ✅ Remover campos que não devem ser copiados (usar destructuring)
      const { id, created_at, updated_at, ...baseEntry } = original;
      
      const futureEntries = dates.slice(1).map(date => ({
        ...baseEntry,
        ...updatesWithCategory,
        entry_date: date,
        recurrence_group_id: recurrenceGroupId,
        // Ajustar due_date e payment_date proporcionalmente
        due_date: original.due_date && original.entry_date 
          ? addDays(date, daysBetween(original.entry_date, original.due_date))
          : null,
        payment_date: original.payment_date && original.entry_date
          ? addDays(date, daysBetween(original.entry_date, original.payment_date))
          : null,
        // ✅ Parcelas futuras sempre são previsão, nunca pagas
        payment_status: 'previsão' as const,
      }));

      console.log('[updateTransaction] Future entries to insert:', futureEntries.length, 'entries');
      console.log('[updateTransaction] First entry sample:', JSON.stringify(futureEntries[0], null, 2));
      console.log('[updateTransaction] Last entry sample:', JSON.stringify(futureEntries[futureEntries.length - 1], null, 2));
      console.log('[updateTransaction] All entry_dates:', futureEntries.map(e => e.entry_date));
      
      const { data: insertedData, error: insertError } = await supabase
        .from('cash_flow_entries')
        .insert(futureEntries)
        .select('id, entry_date, due_date, description');

      if (insertError) {
        console.error('[useTransactions] Error creating future entries:', insertError);
        console.error('[useTransactions] Error details:', JSON.stringify(insertError, null, 2));
        console.error('[useTransactions] Failed entries sample:', JSON.stringify(futureEntries[0], null, 2));
        // Não falhar aqui, o lançamento original já foi atualizado
        console.warn('[useTransactions] Created recurring entry but failed to create future entries');
      } else {
        console.log(`[updateTransaction] ✅ Created ${futureEntries.length} future recurring entries`);
        console.log('[updateTransaction] Inserted entries:', insertedData);
      }
    }

    return data;
  }

  // Atualização normal (não está sendo convertido para recorrente)
  const { data, error } = await supabase
    .from('cash_flow_entries')
    .update(updatesWithCategory)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[useTransactions] Error updating transaction:', error);
    throw new Error(`Erro ao atualizar lançamento: ${error.message}`);
  }

  return data;
}

/**
 * Update recurring transaction with options
 */
export type UpdateRecurringOption = 'single' | 'future' | 'all';

async function updateRecurringTransaction(
  transaction: CashFlowEntry,
  updates: Partial<Omit<CashFlowEntry, 'id' | 'created_at' | 'updated_at'>>,
  option: UpdateRecurringOption
): Promise<{ updatedCount: number }> {
  console.log('[updateRecurringTransaction] Updating with option:', option, transaction);

  // Option 1: Update only this single entry (break it from series)
  if (option === 'single') {
    // ✅ COMPATIBILIDADE: Atualizar category junto com main_category
    const updatesWithCategory = {
      ...updates,
      ...(updates.main_category ? { category: updates.main_category } : {}),
      // Remove from recurrence group to make it independent
      recurrence_group_id: null,
      is_recurring: false,
      frequency: null,
      recurrence_end_date: null,
    };

    const { error } = await supabase
      .from('cash_flow_entries')
      .update(updatesWithCategory)
      .eq('id', transaction.id);

    if (error) {
      console.error('[useTransactions] Error updating single transaction:', error);
      throw new Error(`Erro ao atualizar lançamento: ${error.message}`);
    }

    console.log('[updateRecurringTransaction] Updated single entry');
    return { updatedCount: 1 };
  }

  // Option 2: Update this and all future entries in the series
  if (option === 'future') {
    if (!transaction.recurrence_group_id) {
      throw new Error('Lançamento não possui ID de grupo de recorrência');
    }

    // ✅ COMPATIBILIDADE: Atualizar category junto com main_category
    // ✅ IMPORTANTE: Não copiar payment_status - cada lançamento mantém seu próprio status
    // ✅ IMPORTANTE: Não copiar datas - calcular proporcionalmente para cada ocorrência
    const { payment_status, entry_date, due_date, payment_date, ...updatesWithoutStatus } = updates;
    const updatesWithCategory = {
      ...updatesWithoutStatus,
      ...(updates.main_category ? { category: updates.main_category } : {}),
    };

    console.log('[updateRecurringTransaction] Updating future - removed payment_status and dates from updates');
    console.log('[updateRecurringTransaction] New dates from user:', { due_date, payment_date });
    console.log('[updateRecurringTransaction] Original dates:', { 
      original_due_date: transaction.due_date, 
      original_payment_date: transaction.payment_date 
    });

    // Se há alteração de datas, precisamos buscar todos os lançamentos e atualizar individualmente
    if (due_date !== undefined || payment_date !== undefined) {
      // Buscar todos os lançamentos futuros da série
      const { data: seriesEntries, error: fetchError } = await supabase
        .from('cash_flow_entries')
        .select('*')
        .eq('recurrence_group_id', transaction.recurrence_group_id)
        .gte('entry_date', transaction.entry_date!)
        .order('entry_date', { ascending: true });

      if (fetchError) {
        console.error('[useTransactions] Error fetching future transactions:', fetchError);
        throw new Error(`Erro ao buscar lançamentos futuros: ${fetchError.message}`);
      }

      if (!seriesEntries || seriesEntries.length === 0) {
        return { updatedCount: 0 };
      }

      // ✅ CORREÇÃO: Usar os NOVOS valores do usuário para calcular offset, não os valores originais
      // Se o usuário alterou a data, usar o novo valor; senão, usar o valor original
      const effectiveDueDate = due_date !== undefined ? due_date : transaction.due_date;
      const effectivePaymentDate = payment_date !== undefined ? payment_date : transaction.payment_date;
      
      const dueDateOffset = effectiveDueDate && transaction.entry_date
        ? daysBetween(transaction.entry_date, effectiveDueDate)
        : null;
      const paymentDateOffset = effectivePaymentDate && transaction.entry_date
        ? daysBetween(transaction.entry_date, effectivePaymentDate)
        : null;
      
      console.log('[updateRecurringTransaction] Calculated offsets:', { dueDateOffset, paymentDateOffset });

      // Atualizar cada lançamento individualmente, mantendo as proporções de data
      const updatePromises = seriesEntries.map(async (entry) => {
        const entryUpdates = {
          ...updatesWithCategory,
          // Recalcular due_date proporcionalmente se existir
          due_date: dueDateOffset !== null
            ? addDays(entry.entry_date!, dueDateOffset)
            : entry.due_date,
          // Recalcular payment_date proporcionalmente se existir
          payment_date: paymentDateOffset !== null
            ? addDays(entry.entry_date!, paymentDateOffset)
            : entry.payment_date,
        };

        const { error } = await supabase
          .from('cash_flow_entries')
          .update(entryUpdates)
          .eq('id', entry.id);

        if (error) throw error;
        return entry.id;
      });

      await Promise.all(updatePromises);
      console.log(`[updateRecurringTransaction] Updated ${seriesEntries.length} future entries with proportional dates`);
      return { updatedCount: seriesEntries.length };
    }

    // Se não há alteração de datas, atualizar todos de uma vez
    const { data, error } = await supabase
      .from('cash_flow_entries')
      .update(updatesWithCategory)
      .eq('recurrence_group_id', transaction.recurrence_group_id)
      .gte('entry_date', transaction.entry_date!)
      .select();

    if (error) {
      console.error('[useTransactions] Error updating future transactions:', error);
      throw new Error(`Erro ao atualizar lançamentos futuros: ${error.message}`);
    }

    console.log(`[updateRecurringTransaction] Updated ${data.length} future entries`);
    return { updatedCount: data.length };
  }

  // Option 3: Update all entries in the series
  if (option === 'all') {
    if (!transaction.recurrence_group_id) {
      throw new Error('Lançamento não possui ID de grupo de recorrência');
    }

    // ✅ COMPATIBILIDADE: Atualizar category junto com main_category
    // ✅ IMPORTANTE: Não copiar payment_status - cada lançamento mantém seu próprio status
    // ✅ IMPORTANTE: Não copiar datas - calcular proporcionalmente para cada ocorrência
    const { payment_status, entry_date, due_date, payment_date, ...updatesWithoutStatus } = updates;
    const updatesWithCategory = {
      ...updatesWithoutStatus,
      ...(updates.main_category ? { category: updates.main_category } : {}),
    };

    console.log('[updateRecurringTransaction] Updating all - removed payment_status and dates from updates');
    console.log('[updateRecurringTransaction] New dates from user (all):', { due_date, payment_date });

    // Se há alteração de datas, precisamos buscar todos os lançamentos e atualizar individualmente
    if (due_date !== undefined || payment_date !== undefined) {
      // Buscar todos os lançamentos da série
      const { data: seriesEntries, error: fetchError } = await supabase
        .from('cash_flow_entries')
        .select('*')
        .eq('recurrence_group_id', transaction.recurrence_group_id)
        .order('entry_date', { ascending: true });

      if (fetchError) {
        console.error('[useTransactions] Error fetching all transactions:', fetchError);
        throw new Error(`Erro ao buscar todos os lançamentos: ${fetchError.message}`);
      }

      if (!seriesEntries || seriesEntries.length === 0) {
        return { updatedCount: 0 };
      }

      // ✅ CORREÇÃO: Usar os NOVOS valores do usuário para calcular offset, não os valores originais
      const effectiveDueDate = due_date !== undefined ? due_date : transaction.due_date;
      const effectivePaymentDate = payment_date !== undefined ? payment_date : transaction.payment_date;
      
      const dueDateOffset = effectiveDueDate && transaction.entry_date
        ? daysBetween(transaction.entry_date, effectiveDueDate)
        : null;
      const paymentDateOffset = effectivePaymentDate && transaction.entry_date
        ? daysBetween(transaction.entry_date, effectivePaymentDate)
        : null;
      
      console.log('[updateRecurringTransaction] Calculated offsets (all):', { dueDateOffset, paymentDateOffset });

      // Atualizar cada lançamento individualmente, mantendo as proporções de data
      const updatePromises = seriesEntries.map(async (entry) => {
        const entryUpdates = {
          ...updatesWithCategory,
          // Recalcular due_date proporcionalmente se existir
          due_date: dueDateOffset !== null
            ? addDays(entry.entry_date!, dueDateOffset)
            : entry.due_date,
          // Recalcular payment_date proporcionalmente se existir
          payment_date: paymentDateOffset !== null
            ? addDays(entry.entry_date!, paymentDateOffset)
            : entry.payment_date,
        };

        const { error } = await supabase
          .from('cash_flow_entries')
          .update(entryUpdates)
          .eq('id', entry.id);

        if (error) throw error;
        return entry.id;
      });

      await Promise.all(updatePromises);
      console.log(`[updateRecurringTransaction] Updated ${seriesEntries.length} entries with proportional dates`);
      return { updatedCount: seriesEntries.length };
    }

    // Se não há alteração de datas, atualizar todos de uma vez
    const { data, error } = await supabase
      .from('cash_flow_entries')
      .update(updatesWithCategory)
      .eq('recurrence_group_id', transaction.recurrence_group_id)
      .select();

    if (error) {
      console.error('[useTransactions] Error updating all transactions:', error);
      throw new Error(`Erro ao atualizar todos os lançamentos: ${error.message}`);
    }

    console.log(`[updateRecurringTransaction] Updated all ${data.length} entries`);
    return { updatedCount: data.length };
  }

  throw new Error(`Opção de atualização inválida: ${option}`);
}

/**
 * Delete recurring transaction with options
 */
export type DeleteRecurringOption = 'single' | 'future' | 'all';

async function deleteRecurringTransaction(
  transaction: CashFlowEntry,
  option: DeleteRecurringOption
): Promise<{ deletedCount: number }> {
  console.log('[deleteRecurringTransaction] Deleting with option:', option, transaction);

  // Option 1: Delete only this single entry
  if (option === 'single') {
    const { error } = await supabase
      .from('cash_flow_entries')
      .delete()
      .eq('id', transaction.id);

    if (error) {
      console.error('[useTransactions] Error deleting single transaction:', error);
      throw new Error(`Erro ao excluir lançamento: ${error.message}`);
    }

    console.log('[deleteRecurringTransaction] Deleted single entry');
    return { deletedCount: 1 };
  }

  // Option 2: Delete this and all future entries in the series
  if (option === 'future') {
    if (!transaction.recurrence_group_id) {
      throw new Error('Lançamento não possui ID de grupo de recorrência');
    }

    const { data, error } = await supabase
      .from('cash_flow_entries')
      .delete()
      .eq('recurrence_group_id', transaction.recurrence_group_id)
      .gte('entry_date', transaction.entry_date!)
      .select();

    if (error) {
      console.error('[useTransactions] Error deleting future transactions:', error);
      throw new Error(`Erro ao excluir lançamentos futuros: ${error.message}`);
    }

    console.log(`[deleteRecurringTransaction] Deleted ${data.length} future entries`);
    return { deletedCount: data.length };
  }

  // Option 3: Delete all entries in the series
  if (option === 'all') {
    if (!transaction.recurrence_group_id) {
      throw new Error('Lançamento não possui ID de grupo de recorrência');
    }

    const { data, error } = await supabase
      .from('cash_flow_entries')
      .delete()
      .eq('recurrence_group_id', transaction.recurrence_group_id)
      .select();

    if (error) {
      console.error('[useTransactions] Error deleting all transactions:', error);
      throw new Error(`Erro ao excluir série completa: ${error.message}`);
    }

    console.log(`[deleteRecurringTransaction] Deleted ${data.length} entries in series`);
    return { deletedCount: data.length };
  }

  throw new Error(`Opção de exclusão inválida: ${option}`);
}

/**
 * Delete single transaction (non-recurring or legacy)
 */
async function deleteTransaction(id: string): Promise<void> {
  const { error } = await supabase
    .from('cash_flow_entries')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[useTransactions] Error deleting transaction:', error);
    throw new Error(`Erro ao excluir lançamento: ${error.message}`);
  }
}

/**
 * Main hook
 */
export function useTransactions({ companyIds, userId }: UseTransactionsOptions) {
  const queryClient = useQueryClient();
  
  // Generate consistent query key for multi-company
  const companyKey = Array.isArray(companyIds) 
    ? companyIds.sort().join(',') 
    : companyIds;

  // Query: Fetch transactions
  const transactionsQuery = useQuery({
    queryKey: ['transactions', companyKey],
    queryFn: () => fetchTransactions(companyIds),
    enabled: !!(companyIds && (Array.isArray(companyIds) ? companyIds.length > 0 : true)),
    staleTime: 30000, // Keep data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    placeholderData: (previousData) => previousData, // Keep showing old data while fetching new
  });

  // Mutation: Create transaction
  const createMutation = useMutation({
    mutationFn: (entry: Omit<CashFlowEntry, 'id' | 'created_at' | 'updated_at'>) => {
      // Ensure user_id is included
      const entryWithUserId = {
        ...entry,
        user_id: userId,
      };
      return createTransaction(entryWithUserId);
    },
    onMutate: async (newEntry) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['transactions', companyKey] });

      // Snapshot previous value
      const previousTransactions = queryClient.getQueryData<CashFlowEntry[]>(
        ['transactions', companyKey]
      );

      // Optimistically update cache
      if (previousTransactions) {
        const optimisticEntry: CashFlowEntry = {
          ...newEntry,
          user_id: userId,
          id: 'temp-' + Date.now(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as CashFlowEntry;

        queryClient.setQueryData<CashFlowEntry[]>(
          ['transactions', companyKey],
          [optimisticEntry, ...previousTransactions]
        );
      }

      return { previousTransactions };
    },
    onError: (error, _newEntry, context) => {
      // Rollback on error
      if (context?.previousTransactions) {
        queryClient.setQueryData(
          ['transactions', companyKey],
          context.previousTransactions
        );
      }
      notify.error('Erro ao criar lançamento', error instanceof Error ? error.message : 'Erro desconhecido');
    },
    onSuccess: () => {
      notify.success('Lançamento criado com sucesso!');
    },
    onSettled: () => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['transactions', companyKey] });
      
      // IMPORTANTE: Invalidar também as opções (categorias, parceiros, etc.)
      // para que apareçam nos próximos lançamentos
      const companyIdsList = Array.isArray(companyIds) ? companyIds : [companyIds];
      companyIdsList.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: ['transaction-categories', id] });
        queryClient.invalidateQueries({ queryKey: ['business-partners', id] });
        queryClient.invalidateQueries({ queryKey: ['document-sources', id] });
        queryClient.invalidateQueries({ queryKey: ['payment-methods', id] });
        queryClient.invalidateQueries({ queryKey: ['accounts', id] });
        queryClient.invalidateQueries({ queryKey: ['cost-centers', id] });
        queryClient.invalidateQueries({ queryKey: ['expense-types', id] });
        queryClient.invalidateQueries({ queryKey: ['locations', id] });
        queryClient.invalidateQueries({ queryKey: ['tags', id] });
      });
    },
  });

  // Mutation: Update transaction
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CashFlowEntry> }) =>
      updateTransaction(id, updates),
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: ['transactions', companyKey] });

      const previousTransactions = queryClient.getQueryData<CashFlowEntry[]>(
        ['transactions', companyKey]
      );

      if (previousTransactions) {
        queryClient.setQueryData<CashFlowEntry[]>(
          ['transactions', companyKey],
          previousTransactions.map((t) => (t.id === id ? { ...t, ...updates } : t))
        );
      }

      return { previousTransactions };
    },
    onError: (error, _variables, context) => {
      if (context?.previousTransactions) {
        queryClient.setQueryData(
          ['transactions', companyKey],
          context.previousTransactions
        );
      }
      notify.error('Erro ao atualizar lançamento', error instanceof Error ? error.message : 'Erro desconhecido');
    },
    onSuccess: () => {
      notify.success('Lançamento atualizado com sucesso!');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', companyKey] });
      
      // Invalidar opções também ao atualizar (caso tenha mudado categoria, parceiro, etc.)
      const companyIdsList = Array.isArray(companyIds) ? companyIds : [companyIds];
      companyIdsList.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: ['transaction-categories', id] });
        queryClient.invalidateQueries({ queryKey: ['business-partners', id] });
        queryClient.invalidateQueries({ queryKey: ['document-sources', id] });
        queryClient.invalidateQueries({ queryKey: ['payment-methods', id] });
        queryClient.invalidateQueries({ queryKey: ['accounts', id] });
        queryClient.invalidateQueries({ queryKey: ['cost-centers', id] });
        queryClient.invalidateQueries({ queryKey: ['expense-types', id] });
        queryClient.invalidateQueries({ queryKey: ['locations', id] });
        queryClient.invalidateQueries({ queryKey: ['tags', id] });
      });
    },
  });

  // Mutation: Delete transaction
  const deleteMutation = useMutation({
    mutationFn: deleteTransaction,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['transactions', companyKey] });

      const previousTransactions = queryClient.getQueryData<CashFlowEntry[]>(
        ['transactions', companyKey]
      );

      if (previousTransactions) {
        queryClient.setQueryData<CashFlowEntry[]>(
          ['transactions', companyKey],
          previousTransactions.filter((t) => t.id !== id)
        );
      }

      return { previousTransactions };
    },
    onError: (error, _id, context) => {
      if (context?.previousTransactions) {
        queryClient.setQueryData(
          ['transactions', companyKey],
          context.previousTransactions
        );
      }
      notify.error('Erro ao excluir lançamento', error instanceof Error ? error.message : 'Erro desconhecido');
    },
    onSuccess: () => {
      notify.success('Lançamento excluído com sucesso!');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', companyKey] });
    },
  });

  // Mutation: Delete recurring transaction with options
  const deleteRecurringMutation = useMutation({
    mutationFn: ({ transaction, option }: { transaction: CashFlowEntry; option: DeleteRecurringOption }) =>
      deleteRecurringTransaction(transaction, option),
    onMutate: async ({ transaction, option }) => {
      await queryClient.cancelQueries({ queryKey: ['transactions', companyKey] });

      const previousTransactions = queryClient.getQueryData<CashFlowEntry[]>(
        ['transactions', companyKey]
      );

      // Optimistic update based on option
      if (previousTransactions) {
        let updatedTransactions = previousTransactions;

        if (option === 'single') {
          updatedTransactions = previousTransactions.filter((t) => t.id !== transaction.id);
        } else if (option === 'future' && transaction.recurrence_group_id) {
          updatedTransactions = previousTransactions.filter(
            (t) =>
              t.recurrence_group_id !== transaction.recurrence_group_id ||
              (t.entry_date && transaction.entry_date && t.entry_date < transaction.entry_date)
          );
        } else if (option === 'all' && transaction.recurrence_group_id) {
          updatedTransactions = previousTransactions.filter(
            (t) => t.recurrence_group_id !== transaction.recurrence_group_id
          );
        }

        queryClient.setQueryData<CashFlowEntry[]>(
          ['transactions', companyKey],
          updatedTransactions
        );
      }

      return { previousTransactions };
    },
    onError: (error, _variables, context) => {
      if (context?.previousTransactions) {
        queryClient.setQueryData(
          ['transactions', companyKey],
          context.previousTransactions
        );
      }
      notify.error('Erro ao excluir lançamento', error instanceof Error ? error.message : 'Erro desconhecido');
    },
    onSuccess: (data) => {
      const count = data.deletedCount;
      if (count === 1) {
        notify.success('Lançamento excluído com sucesso!');
      } else {
        notify.success(`${count} lançamentos excluídos com sucesso!`);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', companyKey] });
    },
  });

  // Mutation: Update recurring transaction with options
  const updateRecurringMutation = useMutation({
    mutationFn: ({ transaction, updates, option }: { 
      transaction: CashFlowEntry; 
      updates: Partial<CashFlowEntry>; 
      option: UpdateRecurringOption 
    }) => updateRecurringTransaction(transaction, updates, option),
    onMutate: async ({ transaction, updates, option }) => {
      await queryClient.cancelQueries({ queryKey: ['transactions', companyKey] });

      const previousTransactions = queryClient.getQueryData<CashFlowEntry[]>(
        ['transactions', companyKey]
      );

      // Optimistic update based on option
      if (previousTransactions) {
        let updatedTransactions = previousTransactions;

        if (option === 'single') {
          updatedTransactions = previousTransactions.map((t) => 
            t.id === transaction.id 
              ? { ...t, ...updates, recurrence_group_id: null, is_recurring: false } 
              : t
          );
        } else if (option === 'future' && transaction.recurrence_group_id) {
          updatedTransactions = previousTransactions.map((t) =>
            t.recurrence_group_id === transaction.recurrence_group_id &&
            t.entry_date && transaction.entry_date && t.entry_date >= transaction.entry_date
              ? { ...t, ...updates }
              : t
          );
        } else if (option === 'all' && transaction.recurrence_group_id) {
          updatedTransactions = previousTransactions.map((t) =>
            t.recurrence_group_id === transaction.recurrence_group_id
              ? { ...t, ...updates }
              : t
          );
        }

        queryClient.setQueryData<CashFlowEntry[]>(
          ['transactions', companyKey],
          updatedTransactions
        );
      }

      return { previousTransactions };
    },
    onError: (error, _variables, context) => {
      if (context?.previousTransactions) {
        queryClient.setQueryData(
          ['transactions', companyKey],
          context.previousTransactions
        );
      }
      notify.error('Erro ao atualizar lançamento', error instanceof Error ? error.message : 'Erro desconhecido');
    },
    onSuccess: (data) => {
      const count = data.updatedCount;
      if (count === 1) {
        notify.success('Lançamento atualizado com sucesso!');
      } else {
        notify.success(`${count} lançamentos atualizados com sucesso!`);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', companyKey] });
    },
  });

  return {
    // Data
    transactions: transactionsQuery.data || [],
    
    // States
    isLoading: transactionsQuery.isLoading,
    isError: transactionsQuery.isError,
    error: transactionsQuery.error,
    
    // Mutations
    createTransaction: createMutation.mutate,
    updateTransaction: updateMutation.mutate,
    deleteTransaction: deleteMutation.mutate,
    deleteRecurringTransaction: deleteRecurringMutation.mutate,
    updateRecurringTransaction: updateRecurringMutation.mutate,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending || updateRecurringMutation.isPending,
    isDeleting: deleteMutation.isPending || deleteRecurringMutation.isPending,
    
    // Refetch
    refetch: transactionsQuery.refetch,
  };
}
